import React, { useCallback, useMemo } from "react";
import { useDistributionOptions, Distribution } from "@ls/distribution";
import Drawer from "@ls/react-drawer";
import GroupTypeSelector from "components/GroupTypeSelector";
import GroupSelector from "components/GroupSelector";
import DisplayBenchSwitch from "components/DisplayBenchSwitch";
import LookthruSwitch from "components/LookthruSwitch";
import AsOfDatePicker from "components/AsOfDatePicker";
import PivotsSelector from "components/PivotsSelector";
import PerfReturnsSelector from "components/PerfReturnsSelector";
import AnalyticsSelector from "components/AnalyticsSelector";
import RatingsSelector from "components/RatingsSelector";
import AttributesSelector from "components/AttributesSelector";
import BetasSelector from "components/BetasSelector";
import MiscSelector from "components/MiscSelector";
import TrackingErrorSelector from "components/TrackingErrorSelector";
import CashFlowSelector from "components/CashFlowSelector";
import TradeTrackingErrorSelector from "components/TradeTrackingErrorSelector";
import FactribSelector from "components/FactribSelector";
import ScenarioSelector from "components/ScenarioSelector";
import { startRequests, toggleSelectionDrawer } from "state/app/actions";
import { selectSelectionDrawerOpen } from "state/app/selector";
import { Grid, Cell } from "styled-css-grid";
import styled from "styled-components";
import Radio from "@ls/react-radio-switch";
import { useDispatch, useSelector } from "react-redux";
import {
  selectModeValue,
  selectUncommittedValues,
} from "state/selections/selector";
import SelectionState from "state/selections/SelectionState";
import { commitSelections, setMode } from "state/selections/actions";
import Mode, { modeOptions } from "state/selections/Mode";
import EntitySelector from "components/EntitySelector";
import ScenarioAnalysisSelector from "components/ScenarioAnalysisSelector/ScenarioAnalysisSelector";
import EntitySelectorGrid from "components/EntitySelectorGrid";
import BenchmarkEntitySelector from "components/BenchmarkEntitySelector";
import { isDefined } from "@ls/js-utils";
import { useLocation } from "react-router-dom";
import Scan2Selector from "components/Scan2Selector";
import BenchmarkScenarioSelector from "components/BenchmarkScenarioSelector/BenchmarkScenarioSelector";
import BookAnalyticsSelector from "components/BookAnalyticsSelector";

const Item = styled((props) => <Cell center middle {...props} />)`
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 3px;
  align-self: center;
  &:hover {
    box-shadow:
      0 2px 4px rgba(0, 0, 0, 0.12),
      0 2px 3px rgba(0, 0, 0, 0.23);
  }
`;

const FitItem = styled(Item)`
  width: fit-content;
`;

const Selections = styled.div`
  max-width: 800px;
  min-width: 645px;
  width: 85vw;
  height: 100%;
  position: relative;
  overflow: auto;
`;

const InnerSelectionsScroller = styled.div`
  position: absolute;
  padding: 10px 7px;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const TopRadio = styled(Cell)`
  display: flex;
  flex-direction: row;
  padding: 0 10px;
  border-radius: 3px;
  width: fit-content;
  background: #fff;
  border: 1px solid #ddd;
  &:hover {
    background: rgba(0, 0, 0, 0.01);
  }
`;
const Label = styled.span`
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1.5;
`;

const Selection = () => {
  const dispatch = useDispatch();
  const drawerOpen = useSelector(selectSelectionDrawerOpen);
  const onClickCloseButton = useCallback(
    () => dispatch(toggleSelectionDrawer(false)),
    [dispatch]
  );
  const selectedModeValue = useSelector(
    selectModeValue(SelectionState.UNCOMMITTED)
  );
  const {
    asOfDateValue,
    groupTypeValue,
    groupValue,
    scenarioValue,
    selectedEntityIds,
    displayBenchmarksValue,
  } = useSelector(selectUncommittedValues);

  const onChangeMode = useCallback(
    (mode) => {
      dispatch(setMode(mode));
    },
    [dispatch]
  );

  const { distribution } = useDistributionOptions();
  const { search } = useLocation();
  const hideEntitySelectors = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("hideEntitySelectors") === "true";
  }, [search]);

  const externalUser = React.useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("externalUser") === "true";
  }, [search]);

  const filteredModeOptions = useMemo(() => {
    let result = modeOptions;
    if (distribution === Distribution.vaughannelson) {
      result = modeOptions.filter(({ value }) => value !== Mode.SCENARIOS);
    }
    return result;
  }, [distribution]);

  const onClickGo = useCallback(() => {
    dispatch(toggleSelectionDrawer(false));
    dispatch(commitSelections());
    dispatch(startRequests());
  }, [dispatch]);

  const goButtonProps = useMemo(() => {
    let missingLabels = [];
    let disabled_ = false;
    if (!isDefined(asOfDateValue)) {
      missingLabels.push("As of Date");
      disabled_ = true;
    }
    if (selectedModeValue === Mode.ACC_GROUPS) {
      if (!isDefined(groupTypeValue)) {
        missingLabels.push("Group Type");
        disabled_ = true;
      }
      if (!isDefined(groupValue)) {
        missingLabels.push("Group");
        disabled_ = true;
      }
    } else if (selectedModeValue === Mode.SCENARIOS) {
      if (!isDefined(scenarioValue) || scenarioValue?.length === 0) {
        missingLabels.push("Scenario");
        disabled_ = true;
      }
    } else if (selectedModeValue === Mode.ENTITIES) {
      if (!selectedEntityIds?.length) {
        missingLabels.push("Entities");
        disabled_ = true;
      }
    }

    return {
      disabled: disabled_,
      tooltip: missingLabels.length
        ? `Missing input: ${missingLabels.join(", ")}`
        : undefined,
    };
  }, [
    asOfDateValue,
    groupTypeValue,
    groupValue,
    selectedModeValue,
    selectedEntityIds,
    scenarioValue,
  ]);

  return (
    <Drawer
      active={drawerOpen}
      onClickCloseButton={onClickCloseButton}
      onClickGo={onClickGo}
      goButtonProps={goButtonProps}
      unmountOnExit={false}
      title="Account Summary"
    >
      <Selections>
        <InnerSelectionsScroller>
          <Grid columns={12} gap="8px">
            {!hideEntitySelectors && !externalUser && (
              <TopRadio width={12}>
                <Label>Entity Source:</Label>
                <Radio
                  onChange={onChangeMode}
                  options={filteredModeOptions}
                  value={selectedModeValue}
                  horizontal
                />
              </TopRadio>
            )}
            <FitItem width={4}>
              <AsOfDatePicker />
            </FitItem>
            {!hideEntitySelectors && !externalUser && (
              <>
                {selectedModeValue === Mode.ACC_GROUPS && (
                  <Item width={8}>
                    <GroupTypeSelector />
                  </Item>
                )}
                {selectedModeValue === Mode.ACC_GROUPS && (
                  <Item width={12}>
                    <GroupSelector />
                  </Item>
                )}
                {selectedModeValue === Mode.ENTITIES && (
                  <>
                    <Item width={12}>
                      <EntitySelectorGrid />
                    </Item>
                    <Item width={12}>
                      <EntitySelector />
                    </Item>
                  </>
                )}

                <Item width={12}>
                  <ScenarioSelector />
                </Item>
                <Item width={12}>
                  <BenchmarkScenarioSelector />
                </Item>
              </>
            )}
            {!hideEntitySelectors && !externalUser && (
              <Item width={4}>
                <DisplayBenchSwitch />
              </Item>
            )}
            {!externalUser && (
              <Item width={4}>
                <LookthruSwitch />
              </Item>
            )}
            {displayBenchmarksValue &&
              !hideEntitySelectors &&
              !externalUser && (
                <Item width={12}>
                  <BenchmarkEntitySelector />
                </Item>
              )}
            {!externalUser && (
              <>
                <Item width={12}>
                  <AnalyticsSelector />
                </Item>
                <Item width={12}>
                  <BookAnalyticsSelector />
                </Item>
                <Item width={12}>
                  <AttributesSelector />
                </Item>
                <Item width={12}>
                  <BetasSelector />
                </Item>
                <Item width={12}>
                  <CashFlowSelector />
                </Item>
                <Item width={12}>
                  <FactribSelector />
                </Item>
                <Item width={12}>
                  <PerfReturnsSelector />
                </Item>
                <Item width={12}>
                  <PivotsSelector />
                </Item>
                <Item width={12}>
                  <RatingsSelector />
                </Item>
                <Item width={12}>
                  <TrackingErrorSelector />
                </Item>
                {process.env.REACT_APP_HOST_ENV !== "AZURE" && (
                  <Item width={12}>
                    <TradeTrackingErrorSelector />
                  </Item>
                )}
                {process.env.REACT_APP_HOST_ENV !== "AZURE" && (
                  <Item width={12}>
                    <MiscSelector />
                  </Item>
                )}
                <Item width={12}>
                  <ScenarioAnalysisSelector />
                </Item>
                <Item width={12}>
                  <Scan2Selector />
                </Item>
                <Item width={12}></Item>
              </>
            )}
          </Grid>
        </InnerSelectionsScroller>
      </Selections>
    </Drawer>
  );
};

export default Selection;
